import { useSetRecoilState } from 'recoil';
import { crudUserAtom, usersAtom } from '../atoms/users.atoms';
import { loginAtom } from '../atoms/auth.atoms';
import { toast } from "react-hot-toast";
import { methods } from '../../utils/axios';

// TODO create methods on services/api/users.api.js instead of using methods directly here
const useUsersActions = () => {
  
  const setUserCrud = useSetRecoilState(crudUserAtom);
  const setUsers = useSetRecoilState(usersAtom);
  const setLogin = useSetRecoilState(loginAtom);

  const userList = async (cb) => {
    setUsers((prevValues) => ({ ...prevValues, loading: true }));
    try {
      const response = await methods.get('/users');
      
      if (cb) {
        cb(response.data);
      }
      
      // Filter for super admin users (if needed - this logic was in the original Cognito version)
      let dataUsers = response.data || [];
      
      // Transform backend data to match expected frontend format
      const transformedUsers = dataUsers.map(user => ({
        UserStatus: 'Confirmed', // Default status since they're from our backend
        Username: user.userEmail,
        Enabled: true,
        id: user.id, // Include the backend user ID
        // Módulo de usuarios: empresa, estado de invitación/contraseña y rol para filtrar.
        clientName: user.clientName || '',
        status: user.status || 'active',
        invitationExpiresAt: user.invitationExpiresAt || null,
        isSuperAdmin: !!user.isSuperAdmin,
        isAdmin: !!user.isAdmin,
        isBasic: !!user.isBasic,
        Attributes: [
          { Name: 'email', Value: user.userEmail },
          { Name: 'name', Value: user.name },
          { Name: 'phone_number', Value: user.usersPhone || '' },
          { Name: 'custom:superAdmin', Value: user.isSuperAdmin ? 'true' : 'false' },
          { Name: 'custom:admin', Value: user.isAdmin ? 'true' : 'false' },
          { Name: 'custom:userBasic', Value: user.isBasic ? 'true' : 'false' },
          { Name: 'role', Value: user.role || 'User' }
        ]
      }));
      
      setUsers((prevValues) => ({ ...prevValues, loading: false, payload: transformedUsers || [] }));
    } catch (error) {
      console.error('Error listing users:', error);
      toast.error('Error al listar usuarios');
      setUsers((prevValues) => ({ ...prevValues, loading: false, error }));
    }
  };

  const disableUser = async (data, cb) => {
    setUserCrud((prevValues) => ({ ...prevValues, loading: true }));
    try {
      const response = await methods.del(`/users/${data}`);
      if (cb) {
        cb(response);
      }
      setUserCrud((prevValues) => ({ ...prevValues, loading: false, status: response }));
    } catch (error) {
      console.error('Error disabling user:', error);
      if (cb) {
        cb(error);
      }
      setUserCrud((prevValues) => ({
        ...prevValues,
        loading: false,
        message: 'Error deleting user',
        status: 500,
        error,
      }));
    }
  };

  
  const createUser = async (data, cb) => {
    setUserCrud((prevValues) => ({ ...prevValues, loading: true }));
    try {
      const userData = {
        name: `${data.name} ${data.surname || ''}`.trim(),
        userEmail: data.email,
        usersPhone: data.phoneNumber || ''
      };

      const response = await methods.post('/users', userData);
      if (cb) {
        cb(response);
      }

      setUserCrud((prevValues) => ({
        ...prevValues,
        loading: false,
        status: response,
      }));

    } catch (error) {
      console.error('Error creating user:', error);
      let errorMessage = "Error creating user";
      
      // Handle specific error responses from backend
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        if (error.response.data.error.includes('Email') || error.response.data.error.includes('email')) {
          errorMessage = 'El email ya se encuentra registrado o tiene un formato inválido';
        } else if (error.response.data.error.includes('Name') || error.response.data.error.includes('name')) {
          errorMessage = 'El nombre es inválido o muy largo (máximo 30 caracteres)';
        } else if (error.response.data.error.includes('Phone') || error.response.data.error.includes('phone')) {
          errorMessage = 'El teléfono tiene un formato inválido';
        }
      } else if (error.response?.status === 422) {
        errorMessage = 'Datos inválidos. Verifique la información ingresada';
      } else if (error.response?.status === 500) {
        errorMessage = 'Error interno del servidor';
      }
      
      toast.error(errorMessage, {duration: 3500});
      
      setUserCrud((prevValues) => ({
        ...prevValues,
        loading: false,
        message: errorMessage,
        status: error.response?.status || 500,
        error,
      }));
      if (cb) {
        cb(error);
      }
    }
  }; 

  const updateUserAttributes = async (username, userAttributes, cb) => {
    setUserCrud((prevValues) => ({ ...prevValues, loading: true }));
    try {
      // Transform Cognito attributes format to backend format
      const updateData = {};
      userAttributes.forEach(({ Name, Value }) => {
        switch (Name) {
          case 'name':
            updateData.name = Value;
            break;
          case 'phone_number':
            updateData.usersPhone = Value;
            break;
          case 'custom:admin':
            updateData.isAdmin = Value === 'true';
            break;
          case 'custom:userBasic':
            updateData.isBasic = Value === 'true';
            break;
          // Skip email as it shouldn't be updated
          case 'email':
            break;
          default:
            // Handle any custom attributes if needed
            break;
        }
      });

      const response = await methods.put(`/users/${username}`, updateData);
      
      setUserCrud((prevValues) => ({ ...prevValues, loading: false, status: response }));
      if (cb) {
        cb(response);
      }
    } catch (error) {
      console.error('Error updating user attributes:', error);
      toast.error(error.message || 'Error actualizando usuario');
      setUserCrud((prevValues) => ({
        ...prevValues,
        loading: false,
        message: 'Error updating user attributes',
        status: 500,
        error,
      }));
    }
  };

  const resetUserCrud = () => setUserCrud({ status: 500, message: '', loading: false });

  // Invitar a un administrador o usuario básico de un cliente (mismo endpoint que
  // "Crear Usuario" en el detalle del cliente). Devuelve la respuesta o lanza el error.
  const inviteClientUser = async ({ name, email, phoneNumber, clientId, clientName, isAdmin }) => {
    setUserCrud((prevValues) => ({ ...prevValues, loading: true }));
    try {
      return await methods.post('/users/admin', {
        name,
        userEmail: email,
        usersPhone: phoneNumber || '',
        clientId,
        clientName,
        isAdmin,
        isBasic: !isAdmin,
      });
    } finally {
      setUserCrud((prevValues) => ({ ...prevValues, loading: false }));
    }
  };

  const resendInvitation = (email, name) => methods.post('/users/invitations/resend', { email, name });

  return {
    userList,
    createUser,
    disableUser,
    resetUserCrud,
    updateUserAttributes,
    inviteClientUser,
    resendInvitation,
  };
};

/* TODO what is this function for ?
const allUsersSelector = selector({
  key: 'user',
  get: async () => {
    const cognitoIdentityServiceProvider = new CognitoIdentityServiceProvider();
    const params = {
      UserPoolId: USER_POOL_ID,
    };
    const response = await cognitoIdentityServiceProvider.listUsers(params).promise();
    let allUsers = filter(response?.Users || [], (e) => e.Enabled);
    let dataUsers = allUsers.filter(user => {
      const superAdminAttribute = user.Attributes.find(attr => attr.Name === 'custom:superAdmin');
      return superAdminAttribute && superAdminAttribute.Value === 'true';
    })
    return dataUsers || [];
  },
});

export { allUsersSelector, useUsersActions };
*/
export { useUsersActions };
